<template>
  <div class="ft-root">
    <div class="ft-label">Folders</div>
    <div class="ft-scroll">
      <FolderNode
        v-for="folder in rootFolders"
        :key="folder.id"
        :folder="folder"
        :allFiles="allFiles"
        :currentId="currentId"
        :expandedIds="expandedIds"
        :level="0"
        @navigate="$emit('navigate', $event)"
        @toggle="toggleExpand"
      />
      <div v-if="!rootFolders.length" class="ft-empty">No folders</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineComponent, h, resolveComponent } from 'vue';
import { useFileIcons } from './useFileIcons.js';

const props = defineProps({
  allFiles: { type: Array, default: () => [] },
  currentId: { type: String, default: null },
});

const emit = defineEmits(['navigate']);
const expandedIds = ref(new Set());

const rootFolders = computed(() =>
  props.allFiles.filter(f => f.type === 'folder' && (f.parentId ?? null) === null)
);

function toggleExpand(id) {
  const s = new Set(expandedIds.value);
  s.has(id) ? s.delete(id) : s.add(id);
  expandedIds.value = s;
}
</script>

<!-- Recursive FolderNode defined below as a separate SFC isn't possible in one file,
     so we define it as a global in this file via script setup + defineOptions trick. -->
<script>
// FolderNode recursive component
import { defineComponent, h, computed } from 'vue';
import { useFileIcons } from './useFileIcons.js';

export const FolderNode = defineComponent({
  name: 'FolderNode',
  props: {
    folder: Object,
    allFiles: Array,
    currentId: String,
    expandedIds: Object, // Set
    level: { type: Number, default: 0 },
  },
  emits: ['navigate', 'toggle'],
  setup(props, { emit }) {
    const children = computed(() =>
      props.allFiles.filter(f => f.type === 'folder' && f.parentId === props.folder.id)
    );
    const isExpanded = computed(() => props.expandedIds.has(props.folder.id));
    const isActive = computed(() => props.currentId === props.folder.id);
    const hasChildren = computed(() => children.value.length > 0);

    return () => h('div', { class: 'ft-node-wrap' }, [
      h('div', {
        class: ['ft-node', isActive.value && 'ft-node--active'],
        style: { paddingLeft: (props.level * 14 + 8) + 'px' },
        onClick: () => emit('navigate', props.folder.id),
      }, [
        h('span', {
          class: ['ft-chevron', isExpanded.value && 'ft-chevron--open', !hasChildren.value && 'ft-chevron--hidden'],
          onClick: (e) => { e.stopPropagation(); emit('toggle', props.folder.id); },
        }, [h('i', { class: 'bx bx-chevron-right' })]),
        h('i', { class: ['bx', isExpanded.value ? 'bx-folder-open' : 'bxs-folder', 'ft-folder-icon'] }),
        h('span', { class: 'ft-folder-name' }, props.folder.name),
        children.value.length > 0
          ? h('span', { class: 'ft-badge' }, children.value.length)
          : null,
      ]),
      isExpanded.value && children.value.length
        ? h('div', {}, children.value.map(child =>
            h(FolderNode, {
              key: child.id,
              folder: child,
              allFiles: props.allFiles,
              currentId: props.currentId,
              expandedIds: props.expandedIds,
              level: props.level + 1,
              onNavigate: (id) => emit('navigate', id),
              onToggle: (id) => emit('toggle', id),
            })
          ))
        : null,
    ]);
  },
});
</script>

<style scoped>
.ft-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.ft-label {
  padding: 8px 12px 4px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--fm-text-muted);
  flex-shrink: 0;
}
.ft-scroll {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 8px;
}
.ft-empty {
  padding: 8px 12px;
  font-size: 0.75rem;
  color: var(--fm-text-muted);
}
:deep(.ft-node-wrap) { display: flex; flex-direction: column; }
:deep(.ft-node) {
  display: flex;
  align-items: center;
  gap: 5px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-right: 8px;
  cursor: pointer;
  border-radius: 5px;
  margin: 1px 4px;
  font-size: 0.8rem;
  color: var(--fm-text);
  user-select: none;
  transition: background .1s;
}
:deep(.ft-node:hover) { background: var(--fm-item-hover); }
:deep(.ft-node--active) {
  background: var(--fm-item-selected) !important;
  color: var(--fm-accent);
  font-weight: 600;
}
:deep(.ft-chevron) {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.9rem;
  color: var(--fm-text-muted);
  transition: transform .15s;
  border-radius: 3px;
}
:deep(.ft-chevron:hover) { background: var(--fm-border); }
:deep(.ft-chevron--open i) { transform: rotate(90deg); display: block; }
:deep(.ft-chevron--hidden) { visibility: hidden; }
:deep(.ft-folder-icon) {
  font-size: 1rem;
  color: var(--fm-accent);
  flex-shrink: 0;
}
:deep(.ft-folder-name) {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
:deep(.ft-badge) {
  font-size: 0.6rem;
  background: var(--fm-border);
  color: var(--fm-text-muted);
  border-radius: 8px;
  padding: 1px 5px;
  flex-shrink: 0;
}
</style>
